import { Card } from "~/Configurators/Linear/components/Card";
import { Button, Skeleton, type SkeletonProps, Stack, styled, Typography } from "@mui/material";
import { CORNER_EXTRA_LARGE } from "~/common/components/icon/Corners";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

// Button skeleton component similar to the one in SearchResultSkeleton
const ButtonSkeleton = ({ fullWidth, ...props }: SkeletonProps & { fullWidth?: boolean }) => (
  <Skeleton
    variant="rectangular"
    width={fullWidth ? "100%" : undefined}
    sx={{ borderRadius: CORNER_EXTRA_LARGE }}
    {...props}
  >
    <Button>.</Button>
  </Skeleton>
);

export interface ProductEntrySkeletonProps {
  /**
   * Optional className to apply to the component
   */
  className?: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "64px minmax(0, 1fr)",
  gap: "16px",
  padding: "16px",

  [theme.breakpoints.up("tablet")]: {
    gridTemplateColumns: `64px minmax(0, 1fr) minmax(0, 1fr)`,
  },

  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: `64px minmax(0, 1fr)`,
  },
}));

const ButtonContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  flexDirection: "column",
  gridColumn: "2",
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    gridColumn: "unset",
    alignSelf: "flex-start",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "2",
    flexDirection: "column",
  },
  [theme.breakpoints.up("desktop-l")]: {
    flexDirection: "row",
  },
}));

const ImageSkeleton = styled(Skeleton)({
  width: "64px",
  height: "64px",
});

/**
 * Skeleton component for ProductEntry
 * Displays placeholder UI while product data is loading
 */
export const ProductEntrySkeleton = ({ className }: ProductEntrySkeletonProps) => {
  const isDesktop = useIsMinDesktopSize();

  return (
    <StyledCard className={className || ""}>
      <ImageSkeleton />

      <Stack gap={2}>
        <Typography variant="titleLarge">
          <Skeleton width={"90%"} />
        </Typography>
        <Typography variant="titleLarge">
          <Skeleton width={"70%"} />
        </Typography>
      </Stack>

      <ButtonContainer>
        <Skeleton width={isDesktop ? "45%" : "90%"} height={"40px"} />
        <ButtonSkeleton width={isDesktop ? "70%" : "100%"} />
      </ButtonContainer>
    </StyledCard>
  );
};
